import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Shown when no values are selected. */
  placeholder?: string;
  /** Label format when multiple values are selected. e.g. "{count} selected". */
  countLabel?: (count: number) => string;
  className?: string;
  /** Optional label for the "clear all" affordance. */
  clearLabel?: string;
  disabled?: boolean;
  /** Visual density of the trigger. `sm` is the compact toolbar variant.
   *  Named `uiSize` to match Input/Select, where `size` is native. */
  uiSize?: 'sm' | 'md';
  /** Accessible name. Needed wherever no visible <label> is attached: once a
   *  value is picked the trigger's text becomes the value, so without this
   *  the field it belongs to is unannounced. */
  ariaLabel?: string;
}

/** Pure: case- and diacritic-insensitive, and folds the legacy cedillas (ş ţ)
 *  so typing "stefan" or "ștefan" both find "Ştefan Vodă". */
export function foldForSearch(s: string): string {
  return s
    .toLowerCase()
    .replace(/ş/g, 'ș')
    .replace(/ţ/g, 'ț')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function filterOptions(options: MultiSelectOption[], query: string): MultiSelectOption[] {
  const q = foldForSearch(query.trim());
  if (!q) return options;
  return options.filter((o) => foldForSearch(o.label).includes(q));
}

const triggerSizeClass: Record<'sm' | 'md', string> = {
  sm: 'h-8 px-2 text-sm',
  md: 'h-10 px-3 text-sm',
};

// Position the panel with fixed coordinates so it escapes any
// overflow:hidden / overflow-auto / sticky-table-header / Card stacking
// context. Mirrors the pattern in DropdownMenu.tsx so behaviour is
// consistent across the app.
export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'All',
  countLabel,
  className,
  clearLabel = 'Clear',
  disabled,
  uiSize = 'md',
  ariaLabel,
}: MultiSelectProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  // Place the panel right under (or above, if no room) the trigger.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < 220 && rect.top > 220;
    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      left: rect.left,
      width: rect.width,
    };
    if (openAbove) style.bottom = window.innerHeight - rect.top + 4;
    else style.top = rect.bottom + 4;
    setPanelStyle(style);
  }, [open, value.length]);

  // Outside click + Escape close.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScrollOrResize = () => {
      // Keep panel anchored to the trigger when the user scrolls or resizes.
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelStyle((prev) => ({
        ...prev,
        left: rect.left,
        width: rect.width,
        ...(prev.bottom !== undefined
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      }));
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  // Each opening starts from the full list.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const selectedSet = new Set(value);
  const visible = filterOptions(options, query);

  const toggle = (v: string) => {
    if (selectedSet.has(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const label =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? value[0])
        : countLabel
          ? countLabel(value.length)
          : `${value.length} selected`;

  return (
    <div className={cn('relative inline-block w-full', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-slate-300 bg-white leading-5 shadow-sm',
          triggerSizeClass[uiSize],
          'focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500',
          'dark:border-slate-700 dark:bg-slate-800',
          'dark:focus:border-forest-400 dark:focus:ring-forest-400',
          disabled &&
            'cursor-not-allowed bg-slate-50 text-slate-400 dark:bg-slate-900 dark:text-slate-500',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span
          className={cn(
            'truncate text-left',
            value.length === 0
              ? 'text-slate-500 dark:text-slate-400'
              : 'text-slate-900 dark:text-slate-100',
          )}
        >
          {label}
        </span>
        <span className="ml-2 flex flex-shrink-0 items-center gap-1 text-slate-500 dark:text-slate-400">
          {value.length > 0 ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={clearLabel}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange([]);
                }
              }}
              className="rounded p-0.5 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>

      {open ? (
        <div
          ref={panelRef}
          style={panelStyle}
          className="max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800 dark:shadow-[0_4px_12px_rgba(0,0,0,0.4)]"
        >
          <div className="sticky top-0 border-b border-slate-200 bg-white p-1.5 dark:border-slate-700 dark:bg-slate-800">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Enter picks the first match, so a typed name plus Enter is enough.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (visible[0]) toggle(visible[0].value);
                  }
                }}
                placeholder={t('multiSelect.search', { defaultValue: 'Caută…' })}
                aria-label={t('multiSelect.search', { defaultValue: 'Caută…' })}
                className="h-7 w-full rounded border border-slate-300 bg-white pl-7 pr-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
          </div>
          <div role="listbox" aria-multiselectable="true">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">—</div>
            ) : visible.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                {t('multiSelect.noMatches', { defaultValue: 'Nicio potrivire' })}
              </div>
            ) : (
              visible.map((opt) => {
                const checked = selectedSet.has(opt.value);
                return (
                  <button
                    type="button"
                    key={opt.value}
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggle(opt.value)}
                    className={cn(
                      'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                      checked
                        ? 'bg-forest-50 text-forest-900 hover:bg-forest-100 dark:bg-forest-900/30 dark:text-forest-100 dark:hover:bg-forest-900/50'
                        : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700',
                    )}
                  >
                    <span className="truncate">{opt.label}</span>
                    {checked ? (
                      <Check className="ml-2 h-4 w-4 flex-shrink-0 text-forest-600 dark:text-forest-300" />
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
